import React, { useState, useMemo } from 'react';
import {
  Box,
  Chip,
  Typography,
  Paper,
  Checkbox,
  Tooltip,
  IconButton,
  Button,
} from '@mui/material';
import FlightTakeoffIcon from '@mui/icons-material/FlightTakeoff';
import FlightLandIcon from '@mui/icons-material/FlightLand';
import SnoozeIcon from '@mui/icons-material/Snooze';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { useTranslation } from 'react-i18next';
import { InboxItem, getUrgencyLevel, URGENCY_COLORS, URGENCY_LABELS } from '../types';
import { AssignDropdown } from './AssignDropdown';
import { SnoozePopover } from './SnoozePopover';

export interface TripCardProps {
  item: InboxItem;
  hangars: Array<{ id: string; name: string; width?: number; depth?: number; height?: number }>;
  ramps: Array<{ id: string; name: string }>;
  /** Usage counts per location ID - used to sort quick actions by most used */
  locationUsageCounts?: Map<string, number>;
  onAssignToHangar: (hangarId: string, hangarName: string) => Promise<void>;
  onAssignToRamp: (rampId: string, rampName: string) => Promise<void>;
  onSnooze: (until: Date) => Promise<void>;
  isAssigning?: boolean;
  isSelected?: boolean;
  onToggleSelect?: () => void;
  showCheckbox?: boolean;
}

/**
 * Format date for display - compact
 */
function formatTripDate(dateStr: string | null): string {
  if (!dateStr) return '—';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

/**
 * Format duration between two dates
 */
function formatDuration(arrival: string | null, departure: string | null): string {
  if (!arrival || !departure) return '—';
  const start = new Date(arrival);
  const end = new Date(departure);
  const hours = Math.round((end.getTime() - start.getTime()) / (1000 * 60 * 60));
  if (hours < 24) return `${hours}h`;
  const days = Math.round(hours / 24);
  return `${days}d`;
}

/**
 * Card for unassigned trip items (known aircraft, needs routing)
 * Fixed-width columns for easy scanning
 */
function TripCardComponent({
  item,
  hangars,
  ramps,
  locationUsageCounts,
  onAssignToHangar,
  onAssignToRamp,
  onSnooze,
  isAssigning = false,
  isSelected = false,
  onToggleSelect,
  showCheckbox = false,
}: TripCardProps) {
  const { t } = useTranslation();
  const [snoozeAnchor, setSnoozeAnchor] = useState<HTMLElement | null>(null);
  const [isCompleting, setIsCompleting] = useState(false);
  const [showAllOptions, setShowAllOptions] = useState(false);

  // Calculate urgency based on arrival time
  const urgency = getUrgencyLevel(item.arrival_date);
  const urgencyColor = URGENCY_COLORS[urgency];
  const urgencyLabel = URGENCY_LABELS[urgency];

  // Get top 3 locations for quick actions, sorted by usage (most used first)
  const quickActions = useMemo(() => {
    const allLocations: Array<{ type: 'hangar' | 'ramp'; id: string; name: string; fits: boolean; usageCount: number }> = [];

    // Add all hangars with fit check
    hangars.forEach(hangar => {
      const fits = !item.wingspan || !hangar.width || item.wingspan <= hangar.width;
      allLocations.push({
        type: 'hangar',
        id: hangar.id,
        name: hangar.name,
        fits,
        usageCount: locationUsageCounts?.get(hangar.id) || 0,
      });
    });

    // Add all ramps (always fit)
    ramps.forEach(ramp => {
      allLocations.push({
        type: 'ramp',
        id: ramp.id,
        name: ramp.name,
        fits: true,
        usageCount: locationUsageCounts?.get(ramp.id) || 0,
      });
    });

    // Sort by usage count (most used first) and take top 3
    return allLocations
      .sort((a, b) => b.usageCount - a.usageCount)
      .slice(0, 3);
  }, [hangars, ramps, item.wingspan, locationUsageCounts]);

  const handleAssignToHangar = async (hangarId: string, hangarName: string) => {
    setIsCompleting(true);
    try {
      await onAssignToHangar(hangarId, hangarName);
    } catch {
      setIsCompleting(false);
    }
  };

  const handleAssignToRamp = async (rampId: string, rampName: string) => {
    setIsCompleting(true);
    try {
      await onAssignToRamp(rampId, rampName);
    } catch {
      setIsCompleting(false);
    }
  };

  return (
    <Paper
      elevation={0}
      data-testid={`trip-card-${item.item_id}`}
      data-item-type="unassigned_trip"
      data-tail-number={item.tail_number}
      sx={{
        p: 1.5,
        px: 2,
        borderRadius: 1,
        border: '1px solid',
        borderColor: isSelected ? '#635bff' : 'rgba(0, 0, 0, 0.08)',
        borderLeft: '3px solid',
        borderLeftColor: isSelected ? '#635bff' : (urgencyColor || '#f59e0b'),
        bgcolor: isCompleting
          ? 'rgba(16, 185, 129, 0.04)'
          : isSelected
          ? 'rgba(99, 91, 255, 0.04)'
          : '#fff',
        transition: 'all 150ms ease-out',
        opacity: isCompleting ? 0.7 : 1,
        '&:hover': {
          bgcolor: isSelected ? 'rgba(99, 91, 255, 0.06)' : 'rgba(0, 0, 0, 0.02)',
          borderColor: isSelected ? '#635bff' : 'rgba(0, 0, 0, 0.12)',
        },
      }}
    >
      <Box display="flex" alignItems="center" gap={1}>
        {/* Checkbox - fixed width */}
        {showCheckbox && onToggleSelect && (
          <Checkbox
            checked={isSelected}
            onChange={onToggleSelect}
            size="small"
            sx={{ p: 0, width: 24 }}
          />
        )}

        {/* Urgency Badge */}
        {urgencyLabel && (
          <Chip
            label={urgencyLabel}
            size="small"
            sx={{
              bgcolor: urgencyColor,
              color: 'white',
              fontWeight: 700,
              fontSize: 10,
              height: 20,
              minWidth: 60,
              flexShrink: 0,
            }}
          />
        )}

        {/* Tail Number - fixed width */}
        <Box sx={{ width: 100, flexShrink: 0 }}>
          <Chip
            label={item.tail_number}
            size="small"
            sx={{ fontWeight: 700, fontSize: 13 }}
          />
        </Box>

        {/* Aircraft Model - grows to fill space */}
        <Box sx={{ flex: 1, minWidth: 120 }}>
          <Tooltip title={`${item.aircraft_make || ''} ${item.aircraft_model || ''}`} arrow>
            <Typography
              variant="body2"
              sx={{
                fontSize: 13,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {item.aircraft_make} {item.aircraft_model}
            </Typography>
          </Tooltip>
        </Box>

        {/* Dates */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
          <FlightTakeoffIcon sx={{ color: 'text.secondary', fontSize: 14 }} />
          <Typography variant="body2" fontSize={12}>
            {formatTripDate(item.arrival_date)}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mx: 0.5 }}>→</Typography>
          <FlightLandIcon sx={{ color: 'text.secondary', fontSize: 14 }} />
          <Typography variant="body2" fontSize={12}>
            {formatTripDate(item.departure_date)}
          </Typography>
          <Chip
            label={formatDuration(item.arrival_date, item.departure_date)}
            size="small"
            variant="outlined"
            sx={{ height: 18, fontSize: 10, ml: 0.5 }}
          />
        </Box>

        {/* Completion indicator */}
        {isCompleting && (
          <CheckCircleIcon sx={{ color: '#10b981', fontSize: 24, ml: 2 }} />
        )}

        {/* Quick Action Buttons + More */}
        {!isCompleting && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 2, flexShrink: 0 }}>
            {/* Quick action buttons for common locations */}
            {!showAllOptions && quickActions.map((action, index) => (
              <Tooltip
                key={`${action.type}-${action.id}`}
                title={
                  !action.fits
                    ? t('screens.operationsInbox.tripCard.tooltips.aircraftTooLarge')
                    : index === 0 && action.usageCount > 0
                    ? t('screens.operationsInbox.tripCard.tooltips.mostUsedLocation', { count: action.usageCount })
                    : t('screens.operationsInbox.tripCard.tooltips.assignTo', { name: action.name })
                }
                arrow
              >
                <span>
                  <Button
                    size="small"
                    variant="outlined"
                    disabled={isAssigning || !action.fits}
                    onClick={() => {
                      if (action.type === 'hangar') {
                        handleAssignToHangar(action.id, action.name);
                      } else {
                        handleAssignToRamp(action.id, action.name);
                      }
                    }}
                    sx={{
                      minWidth: 'auto',
                      px: 1,
                      py: 0.25,
                      fontSize: 11,
                      textTransform: 'none',
                      borderColor: index === 0 && action.usageCount > 0 ? 'primary.main' : 'divider',
                      color: index === 0 && action.usageCount > 0 ? 'primary.main' : 'text.primary',
                      fontWeight: index === 0 && action.usageCount > 0 ? 600 : 400,
                      '&:hover': {
                        bgcolor: 'primary.main',
                        color: 'primary.contrastText',
                        borderColor: 'primary.main',
                      },
                      '&.Mui-disabled': {
                        opacity: 0.5,
                      },
                    }}
                  >
                    {action.name}
                  </Button>
                </span>
              </Tooltip>
            ))}

            {/* More button to show full dropdown */}
            {!showAllOptions && (hangars.length + ramps.length > 3) && (
              <Tooltip title={t('screens.operationsInbox.tripCard.tooltips.moreLocations')} arrow>
                <IconButton
                  size="small"
                  onClick={() => setShowAllOptions(true)}
                  sx={{ p: 0.5 }}
                >
                  <MoreHorizIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}

            {/* Full dropdown when "More" is clicked */}
            {showAllOptions && (
              <Box sx={{ width: 150 }}>
                <AssignDropdown
                  item={item}
                  hangars={hangars}
                  ramps={ramps}
                  onAssignToHangar={handleAssignToHangar}
                  onAssignToRamp={handleAssignToRamp}
                  isAssigning={isAssigning}
                />
              </Box>
            )}
          </Box>
        )}

        {/* Snooze Button */}
        {!isCompleting && (
          <Box sx={{ ml: 1, flexShrink: 0 }}>
            <Button
              size="small"
              variant="text"
              startIcon={<SnoozeIcon fontSize="small" />}
              onClick={(e) => setSnoozeAnchor(e.currentTarget)}
              disabled={isAssigning}
              sx={{
                minWidth: 'auto',
                px: 1,
                py: 0.25,
                fontSize: 11,
                textTransform: 'none',
                color: 'text.secondary',
                '&:hover': {
                  bgcolor: 'action.hover',
                },
              }}
            >
              {t('screens.operationsInbox.tripCard.buttons.later')}
            </Button>
            <SnoozePopover
              anchorEl={snoozeAnchor}
              onClose={() => setSnoozeAnchor(null)}
              onSnooze={onSnooze}
            />
          </Box>
        )}
      </Box>
    </Paper>
  );
}

export const TripCard = React.memo(TripCardComponent);
